import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { toast } from "sonner";
import {
  Bot,
  LogIn,
  Quote,
  Send,
  Sparkles,
  Trash2,
  User,
  X,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useChapterContext } from "@/hooks/useChapterContext";
import {
  useChatHistory,
  useChatUsage,
  useClearChatHistory,
  useSendChatMessage,
} from "@/hooks/useChat";
import { AIResponseRetry } from "@/components/chat/AIResponseRetry";
import { TutorLimitModal } from "@/components/chat/TutorLimitModal";
import { useAITutorStore } from "@/stores/ai-tutor-store";
import { renderMarkdownWithMath } from "@/lib/markdown-math";
import { getAiErrorDetails, isAiLimitError, isRetryableAiError } from "@/lib/ai-errors";
import { cn } from "@/lib/cn";
import type { AiQuotaWindow, ChatMessage } from "@/lib/api-types";

const SUGGESTED_PROMPTS = [
  "Explain this concept in simple terms",
  "Give me an example to understand better",
  "What are the key formulas to remember?",
  "Where might this appear in an exam?",
];

const QUOTA_WARN_THRESHOLD = 5;

/**
 * Sliding chat panel for the AI Tutor. Mounted once in StudyShell so
 * every viewer page can summon it via `useAITutorStore.open()`.
 *
 * Layout: bottom-sheet on mobile (88vh, drag-to-dismiss feel via a
 * thumbprint handle), right rail on desktop (480px). The spring stays
 * tight to feel responsive on slower phones.
 *
 * Content pipeline: every message body goes through the same marked +
 * DOMPurify + KaTeX pipeline as the textbook summaries (shared util in
 * lib/markdown-math). Math delimiters supported in both `$...$` /
 * `$$...$$` (textbook-style) and `\(...\)` / `\[...\]` (LLM-style).
 */
export function AITutorSheet() {
  const { chapterId: routeChapterId } = useParams<{ chapterId: string }>();
  const isOpen           = useAITutorStore((s) => s.isOpen);
  const close            = useAITutorStore((s) => s.close);
  const pendingReference = useAITutorStore((s) => s.pendingReference);
  const setPendingRef    = useAITutorStore((s) => s.setPendingReference);
  const consumeRef       = useAITutorStore((s) => s.consumePendingReference);
  const consumePrompt    = useAITutorStore((s) => s.consumePendingPrompt);
  const pinnedChapterId  = useAITutorStore((s) => s.pinnedChapterId);

  // Pinned chapter (set by dashboard mini-prompt etc.) wins over the URL
  // param; falls back to the URL param when both are present and the
  // pinned one matches (no-op). Lets the sheet open with a chapter
  // context from any surface in the app.
  const chapterId = pinnedChapterId ?? routeChapterId;

  const { status } = useAuth();
  const { data: ctx } = useChapterContext(chapterId);
  const { data: me } = useCurrentUser();
  const usage = useChatUsage(isOpen && status === "authenticated");

  // Close on Esc / lock body scroll while open (mobile-friendliness).
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [isOpen, close]);

  return (
    <AnimatePresence>
      {isOpen ? (
        <>
          <motion.div
            key="backdrop"
            className="fixed inset-0 z-[60] bg-black/45 backdrop-blur-[2px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.16 }}
            onClick={close}
          />
          <motion.aside
            key="sheet"
            role="dialog"
            aria-label="AI Tutor"
            data-ai-tutor-sheet
            className={cn(
              "fixed z-[61] bg-surface text-text border-border shadow-2xl flex flex-col",
              // Mobile: bottom sheet, 92vh, rounded top, drag-handle
              "inset-x-0 bottom-0 max-h-[92vh] h-[92vh] rounded-t-2xl border-t",
              // Desktop: right rail, full height, 480px
              "sm:inset-y-0 sm:right-0 sm:left-auto sm:max-h-none sm:h-auto sm:w-[480px] sm:rounded-none sm:border-t-0 sm:border-l",
            )}
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 360, damping: 36, mass: 0.8 }}
            style={{
              // Hardware-accelerate the transform; helps lower-end phones.
              willChange: "transform",
            }}
          >
            <SheetHandle />
            <SheetHeader
              chapterTitle={ctx?.chapter?.chapter_name ?? null}
              subjectName={ctx?.subject?.subject_name ?? null}
              classId={me?.class_id ?? ctx?.chapter?.class_id ?? null}
              remainingToday={usage.data?.tutor.daily.remaining ?? null}
              onClose={close}
            />

            {status === "authenticated" && chapterId ? (
              <AuthedChat
                chapterId={chapterId}
                pendingReference={pendingReference}
                dailyQuota={usage.data?.tutor.daily ?? null}
                onClearReference={() => setPendingRef(null)}
                onConsumeReference={consumeRef}
                onConsumePrompt={consumePrompt}
              />
            ) : (
              <UnauthedState chapterId={chapterId ?? null} />
            )}
          </motion.aside>
        </>
      ) : null}
    </AnimatePresence>
  );
}

// --------------------------------------------------------------------------
function SheetHandle() {
  return (
    <div className="sm:hidden pt-2 pb-1 grid place-items-center">
      <span className="h-1 w-10 rounded-full bg-border" />
    </div>
  );
}

function SheetHeader({
  chapterTitle,
  subjectName,
  classId,
  remainingToday,
  onClose,
}: {
  chapterTitle: string | null;
  subjectName: string | null;
  classId: string | null;
  remainingToday: number | null;
  onClose: () => void;
}) {
  const classLabel = classId ? classId.replace(/^class-/, "Class ") : null;
  return (
    <div className="px-4 sm:px-5 pt-3 sm:pt-5 pb-4 border-b border-border">
      <div className="flex items-start gap-3">
        <span className="grid place-items-center h-9 w-9 rounded-md bg-brand-soft text-brand flex-shrink-0">
          <Bot className="h-5 w-5" />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.16em] text-text-subtle">
            AI Tutor
          </p>
          <h2 className="font-display text-base sm:text-lg font-semibold tracking-tight leading-snug mt-0.5 truncate">
            {chapterTitle || "Chapter Tutor"}
          </h2>
          <div className="mt-1.5 flex items-center gap-1.5 flex-wrap text-[11px] text-text-muted">
            {classLabel ? (
              <span className="px-1.5 py-0.5 rounded-md bg-surface-2 text-text">{classLabel}</span>
            ) : null}
            {subjectName ? (
              <>
                <span className="text-text-subtle">·</span>
                <span>{subjectName}</span>
              </>
            ) : null}
            {remainingToday !== null ? (
              <>
                <span className="text-text-subtle">·</span>
                <span className="rounded-full bg-brand-soft px-2 py-0.5 font-medium text-brand">
                  {remainingToday} left today
                </span>
              </>
            ) : null}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-2 -m-2 rounded-md text-text-muted hover:text-text hover:bg-surface-2 transition-colors"
          aria-label="Close AI Tutor"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
function AuthedChat({
  chapterId,
  pendingReference,
  dailyQuota,
  onClearReference,
  onConsumeReference,
  onConsumePrompt,
}: {
  chapterId: string;
  pendingReference: string | null;
  dailyQuota: AiQuotaWindow | null;
  onClearReference: () => void;
  onConsumeReference: () => string | null;
  onConsumePrompt: () => string | null;
}) {
  const history = useChatHistory(chapterId);
  const send    = useSendChatMessage(chapterId);
  const clear   = useClearChatHistory(chapterId);

  const messages = history.data ?? [];
  const isEmpty  = !history.isLoading && messages.length === 0;
  const latestAssistantMessageId = useMemo(() => {
    const currentMessages = history.data ?? [];
    for (let i = currentMessages.length - 1; i >= 0; i -= 1) {
      if (currentMessages[i].role === "assistant") return currentMessages[i].message_id;
    }
    return null;
  }, [history.data]);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [text, setText] = useState(() => onConsumePrompt() ?? "");
  const [revealForChapterId, setRevealForChapterId] = useState<string | null>(null);
  const errorDetails = getAiErrorDetails<ChatMessage>(send.error);
  const limitError = isAiLimitError(send.error) ? errorDetails : null;
  const retryableError = isRetryableAiError(send.error);
  const resetSend = send.reset;
  const revealingMessageId =
    revealForChapterId === chapterId && send.isSuccess
      ? latestAssistantMessageId
      : null;

  useEffect(() => {
    if (errorDetails?.code !== "RATE_LIMITED_BURST") return;
    toast.warning(
      `You're asking quickly — give me ${errorDetails.retry_after_s ?? 1}s.`,
    );
    resetSend();
  }, [errorDetails, resetSend]);

  // Scroll to bottom whenever messages change OR the typing indicator
  // toggles, so the user always sees the latest.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    // requestAnimationFrame ensures the new content is in the DOM first.
    requestAnimationFrame(() => {
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    });
  }, [messages.length, send.isPending, retryableError]);

  const handleSend = (raw?: string) => {
    const base = (raw ?? text).trim();
    if (!base || send.isPending) return;
    setRevealForChapterId(chapterId);
    const reference = onConsumeReference();
    const composed = reference ? `Regarding: "${reference}"\n\n${base}` : base;
    // chat_message is emitted server-side by staxed_chat_handler, where the quota
    // is spent -- not from here. The client used to be the ONLY source, so any
    // fire-and-forget failure silently dropped a tutor-activity count.
    send.mutate(composed);
    setText("");
  };

  const handleRetry = () => {
    if (!send.variables || send.isPending) return;
    setRevealForChapterId(chapterId);
    send.mutate(send.variables);
  };

  const followTypewriter = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom < 120) el.scrollTop = el.scrollHeight;
  }, []);

  const handleClear = () => {
    if (window.confirm("Clear chat history? This can't be undone.")) {
      clear.mutate();
    }
  };

  const remaining = dailyQuota?.remaining ?? null;

  return (
    <>
      {/* Quota banner (only when low) ----------------------------- */}
      {dailyQuota && remaining !== null && remaining <= QUOTA_WARN_THRESHOLD ? (
        <div className="px-4 sm:px-5 py-2 text-[11px] text-text bg-brand-soft/70 border-b border-border">
          {remaining === 0
            ? "You've used today's tutor allowance. It resets at midnight."
            : `${remaining} ${remaining === 1 ? "message" : "messages"} left today.`}
        </div>
      ) : null}

      {/* Messages -------------------------------------------------- */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-3 sm:px-4 py-4 overscroll-contain"
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        {history.isLoading ? (
          <div className="grid place-items-center h-full text-sm text-text-muted">
            Loading conversation…
          </div>
        ) : isEmpty ? (
          <EmptyState onPick={(p) => handleSend(p)} />
        ) : (
          <ol className="space-y-3">
            {messages.map((m) => (
              <li key={m.message_id}>
                <MessageBubble
                  role={m.role}
                  content={m.content}
                  reveal={m.role === "assistant" && m.message_id === revealingMessageId}
                  onRevealProgress={followTypewriter}
                />
              </li>
            ))}
            {send.isPending ? (
              <li>
                <TypingIndicator />
              </li>
            ) : null}
            {retryableError ? (
              <li>
                <AIResponseRetry onRetry={handleRetry} disabled={send.isPending} />
              </li>
            ) : null}
          </ol>
        )}
      </div>

      {/* Suggested chips (only when not empty -- empty state has its own) */}
      {!isEmpty ? (
        <div className="px-3 sm:px-4 pt-2 border-t border-border bg-surface">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Sparkles className="h-3 w-3 text-brand" />
            <span className="text-[10px] uppercase tracking-[0.14em] text-text-subtle">
              Try
            </span>
          </div>
          <div className="flex gap-1.5 overflow-x-auto pb-2 -mx-1 px-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {SUGGESTED_PROMPTS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => handleSend(p)}
                className="flex-shrink-0 px-3 py-1.5 rounded-full text-xs bg-surface-2 hover:bg-surface-3 text-text-muted hover:text-text border border-border transition-colors"
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {/* Composer -------------------------------------------------- */}
      <div className="border-t border-border bg-surface px-3 sm:px-4 pt-3 pb-[calc(env(safe-area-inset-bottom)+0.75rem)]">
        {pendingReference ? (
          <div className="mb-2 flex items-start gap-2 rounded-lg border border-brand/30 bg-brand-soft/40 px-3 py-2">
            <Quote className="h-3.5 w-3.5 text-brand flex-shrink-0 mt-0.5" />
            <p className="text-xs text-text leading-snug flex-1 line-clamp-3 italic">
              "{pendingReference}"
            </p>
            <button
              type="button"
              onClick={onClearReference}
              className="p-1 -m-1 text-text-muted hover:text-text"
              aria-label="Remove reference"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : null}

        <Composer
          value={text}
          onChange={setText}
          onSubmit={() => handleSend()}
          disabled={send.isPending}
          onClear={messages.length > 0 ? handleClear : undefined}
          isClearing={clear.isPending}
        />
      </div>

      <TutorLimitModal error={limitError} onClose={send.reset} />
    </>
  );
}

function EmptyState({ onPick }: { onPick: (prompt: string) => void }) {
  return (
    <div className="h-full grid place-items-center px-4 py-8">
      <div className="text-center max-w-xs">
        <div className="mx-auto grid place-items-center h-14 w-14 rounded-full bg-brand-soft text-brand mb-3">
          <Bot className="h-7 w-7" />
        </div>
        <h3 className="font-display text-lg font-semibold tracking-tight">
          Hey! I'm your AI Tutor
        </h3>
        <p className="text-sm text-text-muted mt-1.5 leading-relaxed">
          Ask anything about this chapter. I can explain concepts, give
          examples, or help you spot what's likely to come up in an exam.
        </p>
        <div className="mt-5 grid gap-2">
          {SUGGESTED_PROMPTS.slice(0, 3).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => onPick(p)}
              className="w-full px-3 py-2.5 rounded-lg border border-border bg-surface-2 hover:bg-surface-3 text-sm text-left text-text transition-colors"
            >
              {p}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
function Composer({
  value,
  onChange,
  onSubmit,
  disabled,
  onClear,
  isClearing,
}: {
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  disabled: boolean;
  onClear?: () => void;
  isClearing?: boolean;
}) {
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  // Auto-grow the textarea to its content (max 5 lines).
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 5 * 24)}px`;
  }, [value]);

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSubmit(); }}
      className="flex items-end gap-2"
    >
      <div className="flex-1 relative">
        <textarea
          ref={inputRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              onSubmit();
            }
          }}
          rows={1}
          placeholder="Ask about this chapter…"
          disabled={disabled}
          className={cn(
            "w-full resize-none rounded-xl border border-border bg-surface-2 text-text",
            "px-3.5 py-2.5 text-[15px] leading-6",
            "placeholder:text-text-subtle",
            "focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand/60",
            "disabled:opacity-60 disabled:cursor-not-allowed",
            "min-h-[44px] max-h-[120px]",
          )}
        />
      </div>
      {onClear ? (
        <button
          type="button"
          onClick={onClear}
          disabled={isClearing}
          aria-label="Clear conversation"
          className="h-11 w-11 grid place-items-center rounded-xl text-text-muted hover:text-text hover:bg-surface-2 disabled:opacity-60 transition-colors"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      ) : null}
      <button
        type="submit"
        disabled={disabled || value.trim().length === 0}
        aria-label="Send message"
        className={cn(
          "h-11 w-11 grid place-items-center rounded-xl flex-shrink-0",
          "bg-brand text-white shadow-sm",
          "hover:scale-[1.03] active:scale-[0.96] transition-transform",
          "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100",
        )}
      >
        <Send className="h-4 w-4 -ml-0.5" />
      </button>
    </form>
  );
}

function useTypewriterContent(content: string, enabled: boolean) {
  const reduceMotion = useReducedMotion();
  const characters = useMemo(() => Array.from(content), [content]);
  const [visibleCount, setVisibleCount] = useState(() =>
    enabled && !reduceMotion ? 0 : characters.length,
  );

  useEffect(() => {
    if (!enabled || reduceMotion || characters.length === 0) {
      setVisibleCount(characters.length);
      return;
    }

    const durationMs = Math.min(1_800, Math.max(600, characters.length * 5));
    const steps = Math.min(characters.length, Math.ceil(durationMs / 40));
    const charactersPerStep = Math.ceil(characters.length / steps);
    let nextCount = Math.min(charactersPerStep, characters.length);
    setVisibleCount(nextCount);

    const intervalId = window.setInterval(() => {
      nextCount = Math.min(nextCount + charactersPerStep, characters.length);
      setVisibleCount(nextCount);
      if (nextCount >= characters.length) window.clearInterval(intervalId);
    }, durationMs / steps);

    return () => window.clearInterval(intervalId);
  }, [characters, enabled, reduceMotion]);

  return characters.slice(0, visibleCount).join("");
}

// --------------------------------------------------------------------------
function MessageBubble({
  role,
  content,
  reveal = false,
  onRevealProgress,
}: {
  role: "user" | "assistant";
  content: string;
  reveal?: boolean;
  onRevealProgress?: () => void;
}) {
  const isUser = role === "user";
  const visibleContent = useTypewriterContent(content, reveal);
  const html = useMemo(() => renderMarkdownWithMath(visibleContent), [visibleContent]);

  useEffect(() => {
    if (reveal) onRevealProgress?.();
  }, [onRevealProgress, reveal, visibleContent]);

  return (
    <div className={cn("flex gap-2.5", isUser && "flex-row-reverse")}>
      <span
        className={cn(
          "grid place-items-center h-7 w-7 rounded-full flex-shrink-0 mt-0.5",
          isUser ? "bg-brand text-white" : "bg-surface-2 text-text-muted border border-border",
        )}
      >
        {isUser ? <User className="h-3.5 w-3.5" /> : <Bot className="h-3.5 w-3.5" />}
      </span>
      <div
        aria-label={reveal ? content : undefined}
        className={cn(
          "max-w-[85%] rounded-2xl px-3.5 py-2.5",
          isUser
            ? "bg-brand text-white rounded-tr-sm"
            : "bg-surface-2 text-text rounded-tl-sm border border-border",
        )}
      >
        <div
          aria-hidden={reveal ? true : undefined}
          className={cn(
            "text-[14.5px] leading-relaxed break-words",
            "[&_p]:my-1 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0",
            "[&_h1]:font-semibold [&_h1]:text-base [&_h1]:mt-3 [&_h1]:mb-1.5",
            "[&_h2]:font-semibold [&_h2]:text-[15px] [&_h2]:mt-3 [&_h2]:mb-1.5",
            "[&_h3]:font-semibold [&_h3]:text-[14.5px] [&_h3]:mt-2.5 [&_h3]:mb-1",
            "[&_h4]:font-medium [&_h4]:text-[14px] [&_h4]:mt-2.5 [&_h4]:mb-1",
            "[&_h5]:font-medium [&_h5]:text-[13px] [&_h5]:mt-2 [&_h5]:mb-0.5",
            "[&_h6]:font-medium [&_h6]:text-[12px] [&_h6]:mt-2 [&_h6]:mb-0.5",
            "[&_ul]:my-1.5 [&_ul]:pl-5 [&_ul]:list-disc",
            "[&_ol]:my-1.5 [&_ol]:pl-5 [&_ol]:list-decimal",
            "[&_strong]:font-semibold",
            "[&_code]:font-mono [&_code]:text-[0.85em] [&_code]:px-1 [&_code]:rounded",
            "[&_code]:bg-black/10 dark:[&_code]:bg-white/10",
            "[&_pre]:my-2 [&_pre]:p-2.5 [&_pre]:rounded-md [&_pre]:overflow-x-auto",
            "[&_pre]:bg-black/15 dark:[&_pre]:bg-white/10",
            "[&_pre_code]:bg-transparent",
            "[&_.katex-display]:my-2 [&_.katex-display]:overflow-x-auto [&_.katex-display]:overflow-y-hidden",
            // White-on-brand for user; default text colors for assistant
            isUser ? "[&_a]:text-white [&_a]:underline" : "[&_a]:text-brand [&_a]:underline",
          )}
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </div>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="flex gap-2.5">
      <span className="grid place-items-center h-7 w-7 rounded-full flex-shrink-0 mt-0.5 bg-surface-2 border border-border text-text-muted">
        <Bot className="h-3.5 w-3.5" />
      </span>
      <div className="rounded-2xl rounded-tl-sm bg-surface-2 border border-border px-3.5 py-3 flex items-center gap-1">
        <Dot delay={0} />
        <Dot delay={120} />
        <Dot delay={240} />
      </div>
    </div>
  );
}

function Dot({ delay }: { delay: number }) {
  return (
    <motion.span
      className="block h-1.5 w-1.5 rounded-full bg-text-subtle"
      animate={{ y: [0, -3, 0], opacity: [0.4, 1, 0.4] }}
      transition={{ duration: 0.9, repeat: Infinity, delay: delay / 1000, ease: "easeInOut" }}
    />
  );
}

// --------------------------------------------------------------------------
function UnauthedState({ chapterId }: { chapterId: string | null }) {
  const next = chapterId ? `/study/${encodeURIComponent(chapterId)}` : "/subjects";
  return (
    <div className="flex-1 grid place-items-center px-6 py-10 text-center">
      <div className="max-w-xs">
        <div className="mx-auto grid place-items-center h-14 w-14 rounded-full bg-brand-soft text-brand mb-3">
          <Bot className="h-7 w-7" />
        </div>
        <h3 className="font-display text-lg font-semibold tracking-tight">
          Sign in to chat with the tutor
        </h3>
        <p className="text-sm text-text-muted mt-1.5 leading-relaxed">
          The AI Tutor is personal to your account — your conversation
          stays private to you and unlocks instantly after sign in.
        </p>
        <Link
          to={`/auth/login?next=${encodeURIComponent(next)}`}
          className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-brand text-white font-medium text-sm shadow-sm hover:opacity-95 transition-opacity"
        >
          <LogIn className="h-4 w-4" />
          Sign in
        </Link>
      </div>
    </div>
  );
}
